import { stringIncludesInsensitiveAccents } from '../../utils/normalizeForSearch';
import { findTabItemByIds } from './detailUseCase';

/**
 * Return sibling items at a tab level.
 * @param {DetailTabItem[]} tabs - Root tabs from GET
 * @param {string[]} [parentIds] - Ids from root to parent; empty = root level
 * @returns {DetailTabItem[]}
 */
export const getItemsAtLevel = (tabs, parentIds = []) => {
  if (!parentIds?.length) return tabs ?? [];

  const parent = findTabItemByIds(tabs, parentIds);
  return parent?.items ?? [];
};

/**
 * Tabs shown in the level config popover: available but not used in the bar.
 * @param {DetailTabItem[] | null | undefined} items
 * @returns {DetailTabItem[]}
 */
export const getLevelConfigCandidates = (items) => {
  if (!items) return [];
  return items.filter((item) => item.available && !item.used);
};

/**
 * Filter tabs by label or optGroup (case- and accent-insensitive).
 * @param {DetailTabItem[] | null | undefined} items
 * @param {string} search
 * @returns {DetailTabItem[]}
 */
export const filterDetailTabsBySearch = (items, search) => {
  if (!items?.length) return [];

  const searchTrimmed = (search || '').trim();
  if (!searchTrimmed) return items;

  return items.filter((item) => {
    const labelMatch = stringIncludesInsensitiveAccents(item.label, searchTrimmed);
    const optGroupMatch = stringIncludesInsensitiveAccents(item.optGroup, searchTrimmed);
    return labelMatch || optGroupMatch;
  });
};

/**
 * Tabs shown in the gear popover: not available at this level.
 * @param {DetailTabItem[] | null | undefined} items
 * @returns {DetailTabItem[]}
 */
export const getUnavailableItems = (items) => {
  if (!items) return [];
  return items.filter((item) => !item.available);
};

/**
 * Enforce used → available and !available → !used.
 * @param {{ used?: boolean, available?: boolean }} flags
 * @returns {{ used: boolean, available: boolean }}
 */
export const normalizeTabFlags = ({ used, available }) => {
  const normalizedUsed = !!used;
  const normalizedAvailable = !!available;

  if (!normalizedAvailable) {
    return { used: false, available: false };
  }

  if (normalizedUsed) {
    return { used: true, available: true };
  }

  return { used: false, available: true };
};

/**
 * @param {DetailTabItem[]} items
 * @param {string[]} tabIdPath
 * @param {Partial<{ used: boolean, available: boolean }>} flags
 * @returns {DetailTabItem[]}
 */
const updateItemsAtPath = (items, tabIdPath, flags) => {
  if (!tabIdPath.length) return items;

  if (tabIdPath.length === 1) {
    const [targetId] = tabIdPath;
    return items.map((item) => {
      if (item.id !== targetId) return item;
      const nextFlags = normalizeTabFlags({
        used: item.used,
        available: item.available,
        ...flags,
      });
      return { ...item, ...nextFlags };
    });
  }

  const [headId, ...restPath] = tabIdPath;
  return items.map((item) => {
    if (item.id !== headId) return item;
    return {
      ...item,
      items: updateItemsAtPath(item.items ?? [], restPath, flags),
    };
  });
};

/**
 * Update used/available on a tab node (immutable tree).
 * @param {DetailTabItem[]} tabs
 * @param {string[]} tabIdPath - Ids from root to the tab to update
 * @param {Partial<{ used: boolean, available: boolean }>} flags
 * @returns {DetailTabItem[]}
 */
export const updateTabItemFlags = (tabs, tabIdPath, flags) => {
  if (!tabs?.length || !tabIdPath?.length) return tabs ?? [];
  return updateItemsAtPath(tabs, tabIdPath, flags);
};

/**
 * Project full tabs to PartialTabItem shape for POST.
 * @param {DetailTabItem[] | null | undefined} tabs
 * @returns {PartialDetailTabItem[]}
 */
export const mapTabsToPartialTabItems = (tabs) => {
  if (!tabs?.length) return [];

  return tabs.map((tab) => ({
    id: tab.id,
    used: tab.used,
    available: tab.available,
    items: tab.items?.length ? mapTabsToPartialTabItems(tab.items) : null,
  }));
};

/**
 * Build POST body for detail tabs configuration.
 * @param {DetailTabItem[]} tabsLocal
 * @returns {DetailTabsPostRequest}
 */
export const buildDetailTabsPostRequest = (tabsLocal) => ({
  tabItems: mapTabsToPartialTabItems(tabsLocal),
});
